import { useMemo } from 'react';

import { tvpil } from '../compat/tvpil';
import { useInfiniteQueryWithLimit } from '../hooks/useInfiniteQueryWithLimit';
import { useRefetchQuery } from '../hooks/useRefetchQuery';
import { queryKeys } from '../queryKeys';
import { composePaginatedData } from '../utils';

import { fetchCollection } from './fetchCollection';
import { CollectionAsset } from './types';

export type UseCollectionProps = {
  referenceId: string;
  pageSize: number;
  meta?: {
    collectionId: number;
    groupBy?: string;
    isFilterByEntitlementRequired?: boolean;
    kSql?: string;
    limit?: number;
  };
};

export function useCollection({ referenceId, pageSize, meta }: UseCollectionProps) {
  const adultCategoryReferenceId = tvpil.transactionStore.isAdultEntitled
    ? undefined
    : tvpil.dms.adultsConfigurations?.categoryReferenceId;
  const limit = meta?.limit;
  const enabled = Boolean(meta);

  const {
    data,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch: refetchCollection,
  } = useInfiniteQueryWithLimit<CollectionAsset>({
    queryKey: meta ? queryKeys.collection(referenceId, pageSize) : queryKeys.disabled,
    queryFn: ({ pageParam }) => {
      return fetchCollection({
        collectionId: meta!.collectionId,
        pageSize,
        pageIndex: pageParam,
        adultCategoryReferenceId,
        isFilterByEntitlementRequired: meta!.isFilterByEntitlementRequired,
        groupBy: meta!.groupBy,
        kSql: meta!.kSql,
      });
    },
    enabled,
    pageSize,
    limit,
  });

  const items = useMemo(() => {
    if (!data) {
      return [];
    }

    return composePaginatedData({ data, limit });
  }, [data, limit]);

  const totalCount = data ? data.pages[0].totalCount : 0;

  const refetch = useRefetchQuery({ enabled, refetch: refetchCollection });

  return {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };
}
